// ============================================================================
// SCHEMA.JS - CriteriaPack Validation (BLOCK 8: Firewall)
// ============================================================================

// BLOCK 13: Import PHI validation from schemas.js
import {
  validatePackPhiSafety,
  DEFAULT_PHI_POLICY,
  ALLOWED_FIELD_TYPES as PHI_ALLOWED_FIELD_TYPES,
  BLOCKED_FIELD_TYPES as PHI_BLOCKED_FIELD_TYPES,
} from './schemas.js';

/**
 * Valid field types for pack fields
 */
export const FIELD_TYPES = ['text', 'number', 'date', 'select', 'multiselect', 'textarea', 'checkbox', 'radio', 'severity', 'slider'];

/**
 * Valid icon keys (must match available Lucide icons in UI)
 */
export const ICON_KEYS = ['Brain', 'Zap', 'Hand', 'Activity', 'Sparkles', 'Eye', 'Shield', 'AlertTriangle'];

/**
 * Canonical redirect target types.
 * intraSpecialtyRedirectFlags must always use PACK.
 * crossSpecialtyRedirectFlags must always use SPECIALTY.
 */
export const REDIRECT_TARGET_TYPES = {
  PACK: 'pack',
  SPECIALTY: 'specialty',
};

/**
 * Semver regex pattern
 */
const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/;

/**
 * Normalize pack options to standard format [{value, label}]
 * @param {Array} options - Options array (can be strings or objects)
 * @returns {Array<{value: string, label: string}>}
 */
const normalizeOptions = (options) => {
  if (!Array.isArray(options)) return [];
  return options.map((opt, idx) => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    if (typeof opt === 'object' && opt !== null) {
      return {
        value: opt.value || opt.label || `option_${idx}`,
        label: opt.label || opt.value || `Option ${idx + 1}`,
      };
    }
    return { value: `option_${idx}`, label: `Option ${idx + 1}` };
  });
};

/**
 * Validate a single redirect flag item.
 * @param {Object} item
 * @param {string} arrayName - e.g. "intraSpecialtyRedirectFlags"
 * @param {number} index
 * @param {string} requiredTargetType - REDIRECT_TARGET_TYPES.PACK or REDIRECT_TARGET_TYPES.SPECIALTY
 * @param {Set} seenIds - set of already-seen IDs (for duplicate check)
 * @returns {string[]} Array of error strings (empty = valid)
 */
const validateRedirectFlagItem = (item, arrayName, index, requiredTargetType, seenIds) => {
  const errs = [];
  if (!item || typeof item !== 'object') {
    errs.push(`${arrayName}[${index}] must be an object`);
    return errs;
  }
  if (!item.id || typeof item.id !== 'string') {
    errs.push(`${arrayName}[${index}].id must be a non-empty string`);
  } else {
    if (seenIds.has(item.id)) {
      errs.push(`${arrayName}[${index}].id "${item.id}" is duplicated`);
    }
    seenIds.add(item.id);
  }
  if (!item.label || typeof item.label !== 'string') {
    errs.push(`${arrayName}[${index}].label must be a non-empty string`);
  }
  if (!item.description || typeof item.description !== 'string') {
    errs.push(`${arrayName}[${index}].description must be a non-empty string`);
  }
  if (!item.redirectReason || typeof item.redirectReason !== 'string') {
    errs.push(`${arrayName}[${index}].redirectReason must be a non-empty string`);
  }
  if (!item.target || typeof item.target !== 'object') {
    errs.push(`${arrayName}[${index}].target must be an object`);
  } else {
    if (item.target.type !== requiredTargetType) {
      errs.push(
        `${arrayName}[${index}].target.type must be "${requiredTargetType}" ` +
        `(got "${item.target.type || 'undefined'}")`
      );
    }
    if (!item.target.targetId || typeof item.target.targetId !== 'string') {
      errs.push(`${arrayName}[${index}].target.targetId must be a non-empty string`);
    }
    if (!item.target.label || typeof item.target.label !== 'string') {
      errs.push(`${arrayName}[${index}].target.label must be a non-empty string`);
    }
  }
  return errs;
};

/**
 * Normalize a single redirect flag item to canonical shape.
 * @param {Object} item
 * @param {string} requiredTargetType
 * @param {number} idx
 * @returns {Object}
 */
const normalizeRedirectFlagItem = (item, requiredTargetType, idx) => ({
  id: item.id || `rdf_${requiredTargetType}_${idx}`,
  label: (item.label || '').trim(),
  description: (item.description || '').trim(),
  redirectReason: (item.redirectReason || '').trim(),
  target: {
    type: requiredTargetType,
    targetId: (item.target?.targetId || '').trim(),
    label: (item.target?.label || '').trim(),
  },
});

/**
 * Validate a CriteriaPack structure (BLOCK 8: Hard validation + safe defaults)
 * @param {Object} pack - The pack to validate
 * @returns {{ valid: boolean, errors: string[], warnings: string[], normalized: Object|null }}
 */
export const validatePack = (pack) => {
  const errors = [];
  const warnings = [];

  // Top-level structure
  if (!pack || typeof pack !== 'object') {
    return { valid: false, errors: ['Pack must be an object'], warnings: [], normalized: null };
  }

  // ===== METADATA =====
  if (!pack.metadata || typeof pack.metadata !== 'object') {
    errors.push('Missing or invalid "metadata" object');
  } else {
    if (!pack.metadata.id || typeof pack.metadata.id !== 'string' || pack.metadata.id.trim() === '') {
      errors.push('metadata.id must be a non-empty string');
    }
    if (!pack.metadata.name || typeof pack.metadata.name !== 'string' || pack.metadata.name.trim() === '') {
      errors.push('metadata.name must be a non-empty string');
    }
    if (!pack.metadata.specialtyId || typeof pack.metadata.specialtyId !== 'string' || pack.metadata.specialtyId.trim() === '') {
      errors.push('metadata.specialtyId must be a non-empty string (required for analytics)');
    }
    if (!pack.metadata.specialty || typeof pack.metadata.specialty !== 'string' || pack.metadata.specialty.trim() === '') {
      errors.push('metadata.specialty must be a non-empty string');
    }
    if (!pack.metadata.version || !SEMVER_PATTERN.test(pack.metadata.version)) {
      errors.push('metadata.version must be semver format (e.g., "1.0.0")');
    }
    if (pack.metadata.iconKey && !ICON_KEYS.includes(pack.metadata.iconKey)) {
      warnings.push(`metadata.iconKey "${pack.metadata.iconKey}" is not recognized. Using default icon.`);
    }
  }

  // ===== LEGACY redirectFlags warning =====
  if (Array.isArray(pack.redirectFlags) && pack.redirectFlags.length > 0) {
    warnings.push(
      'Legacy "redirectFlags" array detected. Migrate to "intraSpecialtyRedirectFlags" (target.type="pack") ' +
      'and "crossSpecialtyRedirectFlags" (target.type="specialty"). Legacy array will be auto-migrated at read-time.'
    );
  }

  // ===== INTRA-SPECIALTY REDIRECT FLAGS =====
  if (pack.intraSpecialtyRedirectFlags !== undefined) {
    if (!Array.isArray(pack.intraSpecialtyRedirectFlags)) {
      errors.push('intraSpecialtyRedirectFlags must be an array');
    } else {
      const seenIds = new Set();
      pack.intraSpecialtyRedirectFlags.forEach((item, index) => {
        const itemErrors = validateRedirectFlagItem(
          item, 'intraSpecialtyRedirectFlags', index, REDIRECT_TARGET_TYPES.PACK, seenIds
        );
        itemErrors.forEach(e => errors.push(e));
      });
    }
  }

  // ===== CROSS-SPECIALTY REDIRECT FLAGS =====
  if (pack.crossSpecialtyRedirectFlags !== undefined) {
    if (!Array.isArray(pack.crossSpecialtyRedirectFlags)) {
      errors.push('crossSpecialtyRedirectFlags must be an array');
    } else {
      const seenIds = new Set();
      pack.crossSpecialtyRedirectFlags.forEach((item, index) => {
        const itemErrors = validateRedirectFlagItem(
          item, 'crossSpecialtyRedirectFlags', index, REDIRECT_TARGET_TYPES.SPECIALTY, seenIds
        );
        itemErrors.forEach(e => errors.push(e));
      });
    }
  }

  // ===== RED FLAGS =====
  if (!Array.isArray(pack.redFlags)) {
    errors.push('redFlags must be an array');
  } else {
    const redFlagIds = new Set();
    pack.redFlags.forEach((rf, index) => {
      if (!rf || typeof rf !== 'object') {
        errors.push(`redFlags[${index}] must be an object`);
        return;
      }
      if (!rf.id || typeof rf.id !== 'string') {
        errors.push(`redFlags[${index}].id must be a non-empty string`);
      } else {
        if (redFlagIds.has(rf.id)) {
          errors.push(`redFlags[${index}].id "${rf.id}" is duplicated`);
        }
        redFlagIds.add(rf.id);
      }
      if (!rf.label || typeof rf.label !== 'string') {
        errors.push(`redFlags[${index}].label must be a non-empty string`);
      }
      if (rf.severityEnabled !== undefined && typeof rf.severityEnabled !== 'boolean') {
        warnings.push(`redFlags[${index}].severityEnabled should be boolean (defaulting to false)`);
      }
    });
  }

  // ===== FIELDS =====
  if (!Array.isArray(pack.fields)) {
    errors.push('fields must be an array');
  } else if (pack.fields.length === 0) {
    errors.push('fields array must contain at least one field');
  } else {
    const fieldIds = new Set();
    pack.fields.forEach((field, index) => {
      if (!field || typeof field !== 'object') {
        errors.push(`fields[${index}] must be an object`);
        return;
      }
      if (!field.id || typeof field.id !== 'string') {
        errors.push(`fields[${index}].id must be a non-empty string`);
      } else {
        if (fieldIds.has(field.id)) {
          errors.push(`fields[${index}].id "${field.id}" is duplicated`);
        }
        fieldIds.add(field.id);
      }
      if (!field.label || typeof field.label !== 'string') {
        errors.push(`fields[${index}].label must be a non-empty string`);
      }
      if (!field.type || !FIELD_TYPES.includes(field.type)) {
        errors.push(`fields[${index}].type must be one of: ${FIELD_TYPES.join(', ')}`);
      }
      if (['select', 'multiselect', 'radio'].includes(field.type)) {
        if (!Array.isArray(field.options) || field.options.length === 0) {
          errors.push(`fields[${index}] of type "${field.type}" must have a non-empty options array`);
        } else {
          field.options.forEach((opt, optIdx) => {
            if (typeof opt === 'string') {
              // OK
            } else if (typeof opt === 'object' && opt !== null) {
              if (!opt.value && !opt.label) {
                warnings.push(`fields[${index}].options[${optIdx}] should have "value" and "label" properties`);
              }
            } else {
              errors.push(`fields[${index}].options[${optIdx}] must be a string or object with value/label`);
            }
          });
        }
      }
      if (field.type === 'number') {
        if (field.min !== undefined && typeof field.min !== 'number') {
          warnings.push(`fields[${index}].min should be a number`);
        }
        if (field.max !== undefined && typeof field.max !== 'number') {
          warnings.push(`fields[${index}].max should be a number`);
        }
      }
      if (field.type === 'severity') {
        if (field.min === undefined) warnings.push(`fields[${index}] of type "severity" should have min=0 (defaulting to 0)`);
        if (field.max === undefined) warnings.push(`fields[${index}] of type "severity" should have max=10 (defaulting to 10)`);
      }
      if (field.required !== undefined && typeof field.required !== 'boolean') {
        warnings.push(`fields[${index}].required should be boolean (defaulting to false)`);
      }
    });
  }

  // ===== NOT YET STEPS =====
  if (!Array.isArray(pack.notYetSteps)) {
    errors.push('notYetSteps must be an array');
  } else {
    const stepIds = new Set();
    pack.notYetSteps.forEach((step, index) => {
      if (!step || typeof step !== 'object') {
        errors.push(`notYetSteps[${index}] must be an object`);
        return;
      }
      if (!step.stepId || typeof step.stepId !== 'string') {
        errors.push(`notYetSteps[${index}].stepId must be a non-empty string`);
      } else {
        if (stepIds.has(step.stepId)) {
          errors.push(`notYetSteps[${index}].stepId "${step.stepId}" is duplicated`);
        }
        stepIds.add(step.stepId);
      }
      if (!step.label || typeof step.label !== 'string') {
        errors.push(`notYetSteps[${index}].label must be a non-empty string`);
      }
      if (!step.description || typeof step.description !== 'string') {
        errors.push(`notYetSteps[${index}].description must be a non-empty string`);
      }
      if (step.requiredArtifacts !== undefined && !Array.isArray(step.requiredArtifacts)) {
        errors.push(`notYetSteps[${index}].requiredArtifacts must be an array`);
      }
    });
  }

  // ===== PATIENT MESSAGES =====
  if (!pack.patientMessages || typeof pack.patientMessages !== 'object') {
    errors.push('Missing or invalid "patientMessages" object');
  } else {
    ['Routine', 'Urgent', 'NotYet'].forEach(key => {
      if (!pack.patientMessages[key] || typeof pack.patientMessages[key] !== 'string') {
        errors.push(`patientMessages.${key} must be a non-empty string`);
      }
    });
  }

  // ===== PHI SAFETY (BLOCK 13) =====
  const phiValidation = validatePackPhiSafety(pack, DEFAULT_PHI_POLICY);
  if (!phiValidation.valid) {
    phiValidation.errors.forEach(err => errors.push(`[PHI FIREWALL] ${err}`));
  }
  if (phiValidation.warnings?.length > 0) {
    phiValidation.warnings.forEach(warn => warnings.push(`[PHI WARNING] ${warn}`));
  }

  // ===== NORMALIZATION (if valid) =====
  let normalized = null;
  if (errors.length === 0) {
    normalized = normalizePack(pack);
  }

  return { valid: errors.length === 0, errors, warnings, normalized };
};

/**
 * Normalize a pack to ensure all required fields have safe defaults.
 * Handles legacy redirectFlags → migrates to intra/cross arrays.
 * @param {Object} pack - The pack to normalize
 * @returns {Object} - Normalized pack
 */
export const normalizePack = (pack) => {
  // ── Redirect flag migration ──────────────────────────────────────────────
  // New shape: intraSpecialtyRedirectFlags + crossSpecialtyRedirectFlags
  // Legacy shape: redirectFlags (target.type === 'pack' → intra, else → cross)
  const hasNewIntra = Array.isArray(pack.intraSpecialtyRedirectFlags);
  const hasNewCross = Array.isArray(pack.crossSpecialtyRedirectFlags);
  const hasLegacy   = Array.isArray(pack.redirectFlags) && pack.redirectFlags.length > 0;

  let rawIntra = hasNewIntra ? pack.intraSpecialtyRedirectFlags : [];
  let rawCross = hasNewCross ? pack.crossSpecialtyRedirectFlags : [];

  if (!hasNewIntra && !hasNewCross && hasLegacy) {
    // Migrate legacy: route by target.type
    pack.redirectFlags.forEach(rf => {
      if (rf.target?.type === REDIRECT_TARGET_TYPES.PACK) {
        rawIntra.push({ ...rf, target: { ...rf.target, type: REDIRECT_TARGET_TYPES.PACK } });
      } else {
        rawCross.push({ ...rf, target: { ...rf.target, type: REDIRECT_TARGET_TYPES.SPECIALTY } });
      }
    });
  }

  const normalized = {
    metadata: {
      id: pack.metadata.id.trim(),
      name: pack.metadata.name.trim(),
      specialtyId: pack.metadata.specialtyId.trim(),
      specialty: pack.metadata.specialty.trim(),
      version: pack.metadata.version.trim(),
      description: pack.metadata.description || '',
      iconKey: pack.metadata.iconKey || 'Shield',
    },
    intraSpecialtyRedirectFlags: rawIntra.map((item, idx) =>
      normalizeRedirectFlagItem(item, REDIRECT_TARGET_TYPES.PACK, idx)
    ),
    crossSpecialtyRedirectFlags: rawCross.map((item, idx) =>
      normalizeRedirectFlagItem(item, REDIRECT_TARGET_TYPES.SPECIALTY, idx)
    ),
    redFlags: (pack.redFlags || []).map((rf) => ({
      id: rf.id,
      label: rf.label,
      severityEnabled: rf.severityEnabled === true,
    })),
    fields: (pack.fields || []).map((field) => {
      const f = {
        id: field.id,
        label: field.label,
        type: field.type,
        required: field.required === true,
      };
      if (['select', 'multiselect', 'radio'].includes(field.type)) {
        f.options = normalizeOptions(field.options);
      }
      if (field.type === 'number') {
        if (field.min !== undefined) f.min = field.min;
        if (field.max !== undefined) f.max = field.max;
      }
      if (field.type === 'severity') {
        f.min = field.min !== undefined ? field.min : 0;
        f.max = field.max !== undefined ? field.max : 10;
      }
      return f;
    }),
    notYetSteps: (pack.notYetSteps || []).map((step) => ({
      stepId: step.stepId,
      label: step.label,
      description: step.description,
      requiredArtifacts: Array.isArray(step.requiredArtifacts) ? step.requiredArtifacts : [],
    })),
    patientMessages: {
      Routine: pack.patientMessages.Routine,
      Urgent:  pack.patientMessages.Urgent,
      NotYet:  pack.patientMessages.NotYet,
    },
  };

  return normalized;
};

/**
 * Check if a pack is runnable (valid + has required structure)
 * @param {Object} pack
 * @returns {{ runnable: boolean, reason?: string }}
 */
export const isPackRunnable = (pack) => {
  const validation = validatePack(pack);
  if (!validation.valid) {
    return { runnable: false, reason: validation.errors[0] };
  }
  if (pack.fields.length === 0) {
    return { runnable: false, reason: 'Pack has no fields' };
  }
  return { runnable: true };
};

export default { FIELD_TYPES, ICON_KEYS, REDIRECT_TARGET_TYPES, validatePack, normalizePack, isPackRunnable };
